import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TournamentResponse, CreateTournamentRequest } from '../models/tournament.model';
import {
  TournamentDetailResponse,
  UpdateTournamentRequest,
  TournamentBasicResponseWrapper,
  TeamRegistrationsResponseWrapper,
  IndividualRegistrationsResponseWrapper
} from '../models/tournament-detail.model';
import { TeamRegistrationRequest, IndividualRegistrationRequest } from '../models/registration.model';
import { CreateTeamFromIndividualsRequest, AvailableIndividual, UpdateTeamRegistrationRequest, AddPlayerToTeamRequest } from '../models/create-team-from-individuals.model';
import {
  RecordPaymentRequest,
  RecordPaymentResponse,
  PaymentHistoryResponse,
  PaymentSummaryResponse,
  PaymentStatus
} from '../models/payment.model';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/common.model';
import { Match, CreateMatchRequest, UpdateMatchRequest, GenerateGroupMatchesRequest } from '../models/match.model';
import { TournamentGroup } from '../models/group.model';

@Injectable({
  providedIn: 'root'
})
export class TournamentService {
  private apiUrl = `${environment.apiUrl}/tournament`;

  constructor(private http: HttpClient) {}

  getTournaments(): Observable<TournamentResponse> {
    return this.http.get<TournamentResponse>(this.apiUrl);
  }

  getTournamentById(
    id: string,
    paymentStatus?: PaymentStatus,
    branch?: string,
    teamPage: number = 1,
    teamPageSize: number = 10,
    individualPage: number = 1,
    individualPageSize: number = 10
  ): Observable<TournamentDetailResponse> {
    let params = new HttpParams();

    if (paymentStatus !== undefined && paymentStatus !== null) {
      // Convert numeric enum to string name for backend
      params = params.set('paymentStatus', PaymentStatus[paymentStatus]);
    }

    if (branch) {
      params = params.set('branch', branch);
    }

    params = params.set('teamPage', teamPage.toString());
    params = params.set('teamPageSize', teamPageSize.toString());
    params = params.set('individualPage', individualPage.toString());
    params = params.set('individualPageSize', individualPageSize.toString());

    return this.http.get<TournamentDetailResponse>(`${this.apiUrl}/${id}`, { params });
  }

  getTournamentDetails(id: string): Observable<TournamentBasicResponseWrapper> {
    return this.http.get<TournamentBasicResponseWrapper>(`${this.apiUrl}/${id}/details`);
  }

  getTeamRegistrations(
    id: string,
    paymentStatus?: PaymentStatus,
    branch?: string,
    page: number = 1,
    pageSize: number = 10
  ): Observable<TeamRegistrationsResponseWrapper> {
    let params = new HttpParams();

    if (paymentStatus !== undefined && paymentStatus !== null) {
      params = params.set('paymentStatus', PaymentStatus[paymentStatus]);
    }

    if (branch) {
      params = params.set('branch', branch);
    }

    params = params.set('page', page.toString());
    params = params.set('pageSize', pageSize.toString());

    return this.http.get<TeamRegistrationsResponseWrapper>(
      `${this.apiUrl}/${id}/team-registrations`,
      { params }
    );
  }

  getIndividualRegistrations(
    id: string,
    paymentStatus?: PaymentStatus,
    branch?: string,
    page: number = 1,
    pageSize: number = 10
  ): Observable<IndividualRegistrationsResponseWrapper> {
    let params = new HttpParams();

    if (paymentStatus !== undefined && paymentStatus !== null) {
      params = params.set('paymentStatus', PaymentStatus[paymentStatus]);
    }

    if (branch) {
      params = params.set('branch', branch);
    }

    params = params.set('page', page.toString());
    params = params.set('pageSize', pageSize.toString());

    return this.http.get<IndividualRegistrationsResponseWrapper>(
      `${this.apiUrl}/${id}/individual-registrations`,
      { params }
    );
  }

  createTournament(request: CreateTournamentRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(this.apiUrl, request);
  }

  updateTournament(id: string, request: UpdateTournamentRequest): Observable<ApiResponse<null>> {
    return this.http.put<ApiResponse<null>>(`${this.apiUrl}/${id}`, request);
  }

  uploadBanner(id: string, file: File): Observable<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/${id}/upload-banner`, formData);
  }

  registerTeam(request: TeamRegistrationRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${this.apiUrl}/register/team`, request);
  }

  registerIndividual(request: IndividualRegistrationRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${this.apiUrl}/register/individual`, request);
  }

  getAvailableIndividuals(tournamentId: string): Observable<ApiResponse<AvailableIndividual[]>> {
    return this.http.get<ApiResponse<AvailableIndividual[]>>(
      `${this.apiUrl}/${tournamentId}/available-individuals`
    );
  }

  createTeamFromIndividuals(request: CreateTeamFromIndividualsRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(
      `${this.apiUrl}/create-team-from-individuals`,
      request
    );
  }

  updateTeamRegistration(teamRegistrationId: string, request: UpdateTeamRegistrationRequest): Observable<ApiResponse<null>> {
    return this.http.put<ApiResponse<null>>(
      `${this.apiUrl}/registrations/team/${teamRegistrationId}`,
      request
    );
  }

  addPlayerToTeam(teamRegistrationId: string, request: AddPlayerToTeamRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(
      `${this.apiUrl}/registrations/team/${teamRegistrationId}/players`,
      request
    );
  }

  removePlayerFromTeam(teamRegistrationId: string, playerId: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${this.apiUrl}/registrations/team/${teamRegistrationId}/players/${playerId}`
    );
  }

  // Payment methods
  recordTeamPayment(teamRegistrationId: string, request: RecordPaymentRequest): Observable<ApiResponse<RecordPaymentResponse>> {
    return this.http.post<ApiResponse<RecordPaymentResponse>>(
      `${this.apiUrl}/registrations/team/${teamRegistrationId}/payments`,
      request
    );
  }

  recordIndividualPayment(individualRegistrationId: string, request: RecordPaymentRequest): Observable<ApiResponse<RecordPaymentResponse>> {
    return this.http.post<ApiResponse<RecordPaymentResponse>>(
      `${this.apiUrl}/registrations/individual/${individualRegistrationId}/payments`,
      request
    );
  }

  getTeamPaymentHistory(teamRegistrationId: string): Observable<ApiResponse<PaymentHistoryResponse>> {
    return this.http.get<ApiResponse<PaymentHistoryResponse>>(
      `${this.apiUrl}/registrations/team/${teamRegistrationId}/payments`
    );
  }

  getIndividualPaymentHistory(individualRegistrationId: string): Observable<ApiResponse<PaymentHistoryResponse>> {
    return this.http.get<ApiResponse<PaymentHistoryResponse>>(
      `${this.apiUrl}/registrations/individual/${individualRegistrationId}/payments`
    );
  }

  getPaymentSummary(tournamentId: string): Observable<ApiResponse<PaymentSummaryResponse>> {
    return this.http.get<ApiResponse<PaymentSummaryResponse>>(
      `${this.apiUrl}/${tournamentId}/payment-summary`
    );
  }

  // Match methods
  getMatches(tournamentId: string): Observable<ApiResponse<Match[]>> {
    return this.http.get<ApiResponse<Match[]>>(
      `${this.apiUrl}/matches?tournamentId=${tournamentId}`
    );
  }

  createMatch(request: CreateMatchRequest): Observable<ApiResponse<Match>> {
    return this.http.post<ApiResponse<Match>>(`${this.apiUrl}/matches`, request);
  }

  updateMatch(id: string, request: UpdateMatchRequest): Observable<ApiResponse<Match>> {
    return this.http.put<ApiResponse<Match>>(`${this.apiUrl}/matches/${id}`, request);
  }

  deleteMatch(id: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.apiUrl}/matches/${id}`);
  }

  generateGroupMatches(request: GenerateGroupMatchesRequest): Observable<ApiResponse<TournamentGroup>> {
    return this.http.post<ApiResponse<TournamentGroup>>(`${this.apiUrl}/matches/generate-group`, request);
  }

  getGroups(tournamentId: string): Observable<ApiResponse<TournamentGroup[]>> {
    return this.http.get<ApiResponse<TournamentGroup[]>>(
      `${this.apiUrl}/groups?tournamentId=${tournamentId}`
    );
  }
}
